import { PaletteMode } from '@mui/material';
/* eslint-disable react-refresh/only-export-components */
import { createTheme, ThemeProvider as MuiThemeProvider, } from '@mui/material/styles';
import React, { createContext, useEffect, useState } from 'react';
import ArrowDownIcon from 'src/assets/icons/ArrowDownIcon';

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
}

const COLOR_VIOLENT = '#964FF7';
const COLOR_VIOLENT_DARK = '#ccb0ff';
const COLOR_MINT = '#9EFBCD';
const COLOR_MINT_DARK = '#19221d';
const COLOR_DEEP_FOREST = '#00331A';
const COLOR_DEEP_FOREST_BG = '#B6FCD9';
const COLOR_DEEP_FOREST_DARK = '#226c4b';
const COLOR_OBSIDIAN = '#0F0121';
const COLOR_OBSIDIAN_DARK = '#5e08c5';
const COLOR_LILAC = '#E8D6FF';
const COLOR_LILAC_DARK = '#47434f';
const COLOR_ERROR = '#9B0815';
const COLOR_ERROR_DARK = '#cf4e48';
const COLOR_ERROR_BG = '#FDE7E5';
const COLOR_SURFACE = '#F5F5F7';
const COLOR_SURFACE_SECONDARY = '#E6E7EB';
const COLOR_SURFACE_DARK = '#1B0933';
const COLOR_WHITE = '#fff';
const COLOR_DISABLED = '#F7F8FA';
const COLOR_DISABLED_TEXT = '#999A9F';
const COLOR_SURFACE_MEDIUM_TEXT = '#645379';
const COLOR_BORDER_LIGHT = '#DFDFE0';
const COLOR_BORDER_DARK = '#412d5c';
const COLOR_WARNING = '#854D0E';
const COLOR_WARNING_DARK = '#764918';
const COLOR_WARNING_BG = '#FEF9C3';
const COLOR_INFO = '#DBEAFE';
const COLOR_INFO_DARK = '#1E40AF';

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as PaletteMode) || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const palette = {
    mode,
    action: {
      disabledBackground: mode === 'light' ? COLOR_DISABLED : COLOR_LILAC_DARK,
      focus: mode === 'light' ? COLOR_VIOLENT_DARK : COLOR_VIOLENT,
      hover: COLOR_VIOLENT_DARK,
      hoverOpacity: 0.2,
      selected: COLOR_VIOLENT,
      selectedOpacity: 0.1,
    },
    primary: {
      main: mode === 'light' ? COLOR_VIOLENT : COLOR_VIOLENT_DARK,
    },
    success: {
      main: COLOR_DEEP_FOREST_BG,
      dark: mode === 'light' ? COLOR_DEEP_FOREST : COLOR_DEEP_FOREST_DARK,
      light: mode === 'light' ? COLOR_DEEP_FOREST : COLOR_DEEP_FOREST_DARK,
      contrastText: mode === 'light' ? COLOR_DEEP_FOREST : COLOR_DEEP_FOREST_DARK,
    },
    error: {
      main: COLOR_ERROR_BG,
      dark: mode === 'light' ? COLOR_ERROR : COLOR_ERROR_DARK,
      light: mode === 'light' ? COLOR_ERROR : COLOR_ERROR_DARK,
      contrastText: mode === 'light' ? COLOR_ERROR : COLOR_ERROR_DARK,
    },
    warning: {
      main: COLOR_WARNING_BG,
      dark: mode === 'light' ? COLOR_WARNING : COLOR_WARNING_DARK,
      light: mode === 'light' ? COLOR_WARNING : COLOR_WARNING_DARK,
      contrastText: mode === 'light' ? COLOR_WARNING : COLOR_WARNING_DARK,
    },
    info: {
      main: COLOR_INFO,
      dark: COLOR_INFO_DARK,
      light: COLOR_INFO_DARK,
      contrastText: COLOR_INFO_DARK,
    },
    custom: {
      border: mode === 'light' ? COLOR_BORDER_LIGHT : COLOR_BORDER_DARK,
      error: COLOR_ERROR,
      errorBg: COLOR_ERROR_BG,
      violent: mode === 'light' ? COLOR_VIOLENT : COLOR_VIOLENT_DARK,
      mint: mode === 'light' ? COLOR_MINT : COLOR_MINT_DARK,
      deepForest: mode === 'light' ? COLOR_DEEP_FOREST : COLOR_DEEP_FOREST_DARK,
      obsidian: mode === 'light' ? COLOR_OBSIDIAN : COLOR_OBSIDIAN_DARK,
      lilac: COLOR_LILAC,
      white: COLOR_WHITE,
      surfaceDark: COLOR_SURFACE_DARK,
    },
    ...(mode === 'light'
      ? {
        DataGrid: {
          bg: COLOR_WHITE,
          pinnedBg: COLOR_WHITE,
          headerBg: COLOR_WHITE,
        },
        background: {
          default: COLOR_SURFACE,
          paper: COLOR_WHITE,
        },
        text: {
          primary: COLOR_SURFACE_DARK,
          secondary: COLOR_SURFACE_MEDIUM_TEXT,
          disabled: COLOR_DISABLED_TEXT,
        },
        neutral: {
          main: COLOR_SURFACE,
          dark: COLOR_SURFACE_SECONDARY,
        },
      }
      : {
        DataGrid: {
          bg: COLOR_SURFACE_DARK,
          pinnedBg: COLOR_SURFACE_DARK,
          headerBg: COLOR_SURFACE_DARK,
        },
        background: {
          default: COLOR_OBSIDIAN,
          paper: COLOR_SURFACE_DARK,
        },
        text: {
          primary: COLOR_WHITE,
          secondary: COLOR_SURFACE,
          disabled: COLOR_DISABLED_TEXT,
        },
        neutral: {
          main: '#412d5c',
          dark: '#3c0882',
        },
      }),
  };

  const theme = createTheme({
    palette,
    typography: {
      fontFamily:
        'Archivo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '32px',
        lineHeight: 'normal',
        letterSpacing: '-0.02em', // -2%
      },
      h2: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '24px',
        lineHeight: 'normal',
        letterSpacing: '-0.02em', // -2%
      },
      h3: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: 'normal',
        letterSpacing: '-0.02em', // -2%
      },
      body1: {
        fontFamily: 'Archivo',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
      },
      body2: {
        fontFamily: 'Archivo',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
      },
      caption: {
        fontFamily: 'Archivo',
        fontWeight: 800,
        fontSize: '12px',
        lineHeight: '16px',
      },
    },
    components: {
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: '20px',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'background.paper',
          },
          elevation1: {
            boxShadow: '0 4px 40px 0 rgba(18, 13, 26, 0.10)',
          }
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          outlined: {
            color: mode === 'light' ? COLOR_SURFACE_DARK : COLOR_WHITE,
            borderColor: COLOR_BORDER_LIGHT,
            background: palette.background.paper,
          }
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            backgroundColor: palette.background.paper,
            border: `1px solid ${palette.custom.border}`,
            padding: '0 16px',
            gap: '8px',
            overflow: 'hidden',
            '&:hover': {
              borderColor: palette.custom.border,
            },
            '&.Mui-focused': {
              borderColor: palette.primary.main,
              boxShadow: `0 0 0 2px ${COLOR_LILAC}`,
            },
            '&.Mui-error': {
              border: `1px solid ${COLOR_ERROR}`,
              '&:hover': {
                borderColor: COLOR_ERROR,
              },
              '&.Mui-focused': {
                borderColor: COLOR_ERROR,
                boxShadow: `0 0 0 2px ${COLOR_ERROR_BG}`,
              },
            }
          },
          input: {
            padding: '10px 0',
          },
          notchedOutline: {
            border: 'none'
          }
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
          },
          sizeSmall: {
            height: '20px',
          },
          sizeMedium: {
            height: '32px',
            fontSize: '14px',

            '& .MuiChip-label': {
              paddingLeft: '8px',
              paddingRight: '8px',
            }
          },
        },
      },

      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '56px',
            '@media (min-width:600px)': {
              minHeight: '60px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '8px 0',
            borderBottom: 'none',
            fontSize: 16,
          },
          head: {
            fontSize: 14,
            fontWeight: 400,
            color: palette.text.secondary,
          }
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            padding: '0 0 8px',
            borderBottom: 'none',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          IconComponent: ArrowDownIcon,
          MenuProps: {
            disableScrollLock: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            }
          }
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            background: palette.background.paper,
          },
          icon: {
            top: 0,
            bottom: 0,
            right: 12,
            margin: 'auto',
          }
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              fontWeight: 600,
            }
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {},
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            '& .MuiBox-root': {
              borderRadius: '24px',
            }
          }
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 400,
            color: palette.custom.lilac,
            backgroundColor: palette.custom.surfaceDark,
          }
        }
      },
      // @ts-ignore
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.custom.border}`,
            borderRadius: '20px',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 400,
            },
            '& .MuiDataGrid-columnSeparator > svg': {
              display: 'none',
            },
            '& .MuiDataGrid-cell': {
              border: 'none',
              borderBottom: `1px solid ${palette.custom.border}`,
              paddingTop: 16,
              paddingBottom: 16,
              alignItems: 'center',
            },
            '& .MuiDataGrid-cellCheckbox': {
              paddingTop: 7,
              paddingBottom: 7,
            },
            '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox, & .sticky-cell, & .sticky-cell-header': {
              position: 'sticky',
              left: 0,
              zIndex: 1,
              background: 'inherit',
            },
            '& .sticky-cell, & .sticky-cell-header': {
              left: 50,
              borderRight: `1px solid ${palette.custom.border}`,
            },
            '& .MuiDataGrid-row:hover:not(.Mui-selected)': {
              background: 'inherit',
            },
            '& .MuiDataGrid-row:nth-of-type(odd):not(.Mui-selected), & .sticky-cell-header, & .MuiDataGrid-columnHeaderCheckbox': {
              background: palette.background.paper
            },
            '& .MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
              background: palette.background.default
            },
          }
        }
      }
    },
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
